import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
  Sparkles, TrendingDown, Target, AlertTriangle, FlaskConical, Gauge,
  Mail, FileDown, ArrowRight, Check, Quote, ChevronRight, Wallet,
  ArrowUpRight, ShieldCheck
} from 'lucide-react';

// Small inline component for the animated scenario simulator
const ScenarioSimulator = () => {
  const [hire, setHire] = useState(5000);
  const [revenue, setRevenue] = useState(0);
  const baselineCash = 60000;
  const baselineBurn = 8000;
  const scenarioBurn = baselineBurn + hire - revenue;
  const baselineRunway = (baselineCash / baselineBurn).toFixed(1);
  const scenarioRunway = scenarioBurn > 0 ? (baselineCash / scenarioBurn).toFixed(1) : '∞';
  const runwayDelta = scenarioBurn > 0 ? (parseFloat(scenarioRunway) - parseFloat(baselineRunway)).toFixed(1) : null;

  return (
    <div className="bg-white rounded-lg p-6 border border-[#E6E8E6] shadow-sm" data-testid="scenario-simulator">
      <div className="flex items-center gap-2 mb-4">
        <FlaskConical className="w-5 h-5 text-[#0A2B1D]" />
        <h3 className="font-semibold text-[#111614]">Try it: what if you hire someone?</h3>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-xs text-[#5C6661] mb-1.5 block">Monthly expense Δ ($)</label>
          <input
            type="range" min="0" max="20000" step="500" value={hire}
            onChange={(e) => setHire(parseInt(e.target.value))}
            className="w-full accent-[#0A2B1D]"
            data-testid="sim-expense-slider"
          />
          <div className="text-sm font-bold text-[#0A2B1D] mt-1">+${hire.toLocaleString()}/mo</div>
        </div>
        <div>
          <label className="text-xs text-[#5C6661] mb-1.5 block">New revenue Δ ($)</label>
          <input
            type="range" min="0" max="20000" step="500" value={revenue}
            onChange={(e) => setRevenue(parseInt(e.target.value))}
            className="w-full accent-emerald-600"
            data-testid="sim-revenue-slider"
          />
          <div className="text-sm font-bold text-emerald-700 mt-1">+${revenue.toLocaleString()}/mo</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded bg-[#F6F5F2] border border-[#E6E8E6]">
          <div className="text-xs uppercase text-[#5C6661] mb-1">Baseline</div>
          <div className="text-xl font-bold text-[#111614]">{baselineRunway} mo</div>
          <div className="text-xs text-[#5C6661]">${baselineBurn.toLocaleString()}/mo burn</div>
        </div>
        <div className="p-3 rounded bg-[#E2ECE7] border border-[#0A2B1D]/20">
          <div className="text-xs uppercase text-[#0A2B1D] mb-1 font-medium">New scenario</div>
          <div className="text-xl font-bold text-[#0A2B1D]">{scenarioRunway} mo</div>
          <div className="text-xs text-[#5C6661]">${scenarioBurn > 0 ? scenarioBurn.toLocaleString() : 0}/mo burn</div>
          {runwayDelta !== null && (
            <div className={`text-xs mt-1.5 font-medium ${parseFloat(runwayDelta) < 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="sim-runway-delta">
              {parseFloat(runwayDelta) > 0 ? '+' : ''}{runwayDelta} months
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-[#5C6661] mt-4 text-center">Live preview · Drag the sliders</p>
    </div>
  );
};

// Sample weekly brief preview
const WeeklyBriefSample = () => (
  <div className="bg-white rounded-lg overflow-hidden border border-[#E6E8E6] shadow-sm" data-testid="sample-weekly-brief">
    <div className="bg-[#0A2B1D] px-6 py-4">
      <div className="flex items-center gap-2">
        <Mail className="w-5 h-5 text-white" />
        <span className="text-white font-semibold">Your weekly AI Finance Brief</span>
      </div>
      <p className="text-xs text-[#B4C9BE] mt-1">3 things to know · Sample preview</p>
    </div>
    <div className="p-6 space-y-3">
      {[
        { title: 'Your software spend jumped 38% this month', body: 'Adobe + Notion + Figma now $890/mo vs $645 last month. The biggest driver is a new Figma seat. Worth auditing — could you consolidate seats or move to annual billing?' },
        { title: 'Revenue is up but burn outpaced it', body: 'You added $4,200 MRR (great!) but expenses rose $5,800. Net burn widened by $1,600. Watch this trend — two more months and runway drops below 6.' },
        { title: 'Your runway is 8.4 months at current pace', body: '$67,200 cash · $8,000/mo burn. You\'re in healthy territory but not abundant. Consider locking 1 large contract or trimming $500/mo of non-core SaaS to extend to 9+.' },
      ].map((item, idx) => (
        <div key={idx} className="flex gap-3 p-3 rounded bg-[#F6F5F2] border-l-4 border-[#0A2B1D]">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white flex items-center justify-center text-xs font-bold text-[#0A2B1D]">{idx + 1}</div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-[#111614]">{item.title}</div>
            <p className="text-xs text-[#5C6661] mt-1 leading-relaxed">{item.body}</p>
          </div>
        </div>
      ))}
      <div className="text-xs text-[#5C6661] text-center pt-2 border-t border-[#E6E8E6]">
        Sent every 7 days · Cash $67,200 · Burn $8K/mo · 8.4 months runway
      </div>
    </div>
  </div>
);

// Sample board pack preview
const BoardPackSample = () => (
  <div className="bg-white rounded-lg overflow-hidden border border-[#E6E8E6] shadow-sm" data-testid="sample-board-pack">
    <div className="bg-gradient-to-br from-[#0A2B1D] to-[#254D3D] px-6 py-4">
      <div className="flex items-center gap-2">
        <FileDown className="w-5 h-5 text-white" />
        <span className="text-white font-semibold">Board Pack PDF · Sample</span>
      </div>
      <p className="text-xs text-[#B4C9BE] mt-1">One click · Investor-ready · AI-written summary</p>
    </div>
    <div className="p-6 space-y-4">
      <div>
        <div className="text-xs uppercase tracking-wide text-[#5C6661] mb-1">Executive Summary</div>
        <p className="text-sm text-[#111614] leading-relaxed italic">
          "Strong revenue month (+12% MoM), but expense growth outpaced it. Margins held at 38%. Runway healthy at 8.4 mo. Single most important action: lock the Q3 enterprise pipeline to push past breakeven."
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="p-2 rounded bg-[#F6F5F2]">
          <div className="text-xs text-[#5C6661]">MRR</div>
          <div className="font-bold text-[#111614]">$38,200 <span className="text-xs text-emerald-700">+12%</span></div>
        </div>
        <div className="p-2 rounded bg-[#F6F5F2]">
          <div className="text-xs text-[#5C6661]">Gross Margin</div>
          <div className="font-bold text-[#111614]">38.4%</div>
        </div>
        <div className="p-2 rounded bg-[#F6F5F2]">
          <div className="text-xs text-[#5C6661]">Burn</div>
          <div className="font-bold text-[#111614]">$8,000/mo</div>
        </div>
        <div className="p-2 rounded bg-[#F6F5F2]">
          <div className="text-xs text-[#5C6661]">Runway</div>
          <div className="font-bold text-[#111614]">8.4 months</div>
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-[#5C6661] mb-1">Top Expense Categories</div>
        <div className="space-y-1 text-xs">
          {[
            { name: 'Payroll', amt: 14400 },
            { name: 'Software & SaaS', amt: 890 },
            { name: 'Marketing', amt: 720 },
          ].map((c) => (
            <div key={c.name} className="flex justify-between"><span className="text-[#5C6661]">{c.name}</span><span className="font-medium text-[#111614]">${c.amt.toLocaleString()}</span></div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const CFOHub = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);

  useEffect(() => {
    setToken(sessionStorage.getItem('mybookie_token'));
    // Set per-route SEO tags directly (more reliable than Helmet across hydration edge cases)
    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    document.title = 'CFO Hub — Runway, Burn, AI Insights | MyBookie';
    setMeta('link[rel="canonical"]', 'href', 'https://mybookie.ai/cfo');
    setMeta('meta[name="description"]', 'content', "Most bookkeeping apps stop at 'here are your numbers.' MyBookie tells you what to do. Runway, burn rate, budget tracking, anomaly detection, scenario planning, AI commentary, and a weekly Finance Brief — built in.");
    setMeta('meta[property="og:title"]', 'content', 'MyBookie CFO Hub — Out-of-the-box CFO insights for solopreneurs');
    setMeta('meta[property="og:description"]', 'content', 'Runway, burn rate, scenario planning, AI Finance Brief, and Board Pack PDF — built into your bookkeeping app.');
    setMeta('meta[property="og:url"]', 'content', 'https://mybookie.ai/cfo');
    setMeta('meta[name="twitter:title"]', 'content', 'MyBookie CFO Hub — Runway, Burn & AI Insights');
    setMeta('meta[name="twitter:description"]', 'content', 'Out-of-the-box CFO insights for solopreneurs and startups.');
  }, []);

  const handleCTA = () => {
    if (token) navigate('/dashboard?tab=cfo');
    else navigate('/?signup=1&from=cfo');
  };

  const features = [
    {
      icon: <Wallet className="w-6 h-6" />,
      title: 'Runway & Burn Rate',
      desc: 'How many months until your cash runs out — at a glance. 3-month rolling burn, MoM trend, and a clear status badge (profitable / healthy / warning / critical).'
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Budget vs Actuals',
      desc: 'Set monthly budgets per category. We track variance in real time with color-coded bars so you catch overspend before month-end, not after.'
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: 'Anomaly Detection',
      desc: 'Catches duplicate charges, vendor spending spikes >50%, and unusually large new-vendor payments automatically. No rules to configure.'
    },
    {
      icon: <FlaskConical className="w-6 h-6" />,
      title: 'Scenario Planning',
      desc: '"What if I hire someone?" "What if I cut Adobe?" "What if I raise $50K?" Drag the sliders and see your runway change in real time.'
    },
    {
      icon: <Gauge className="w-6 h-6" />,
      title: 'KPI Dashboard + AI Commentary',
      desc: 'MRR, gross margin, burn multiple, net income — with a written take from your AI CFO explaining what changed and what to do.'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Weekly AI Finance Brief',
      desc: 'Every 7 days, your AI CFO emails you the 3 most important things to know about your finances. Personalized, actionable, no fluff.'
    },
  ];

  return (
    <div className="min-h-screen bg-[#F6F5F2] text-[#111614]">
      {/* SEO meta tags managed via direct DOM in useEffect (more reliable than Helmet for static-shell apps) */}

      {/* Hero */}
      <section className="bg-[#0A2B1D] text-white px-6 py-20">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-2 rounded-sm text-sm text-[#B4C9BE] mb-8">
            <Sparkles className="w-4 h-4" /> CFO Hub · A MyBookie feature
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium mb-6 leading-tight tracking-tight">
            Most bookkeeping apps tell you<br />
            <span className="text-[#B4C9BE]">what happened.</span>
          </h1>
          <h2 className="text-3xl md:text-4xl font-medium mb-8 leading-tight">
            MyBookie tells you <span className="underline decoration-[#B4C9BE]">what to do.</span>
          </h2>
          <p className="text-lg md:text-xl text-[#B4C9BE] mb-10 max-w-2xl mx-auto leading-relaxed">
            Runway, burn rate, scenario planning, anomaly detection, AI commentary, and a weekly Finance Brief — all built into your bookkeeping. No spreadsheets. No add-ons. No extra cost.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleCTA} className="bg-white text-[#0A2B1D] hover:bg-[#B4C9BE] rounded-sm h-12 px-8 text-base" data-testid="cfo-hub-cta-primary">
              {token ? 'Open CFO Mode' : 'Try it free'} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => document.getElementById('cfo-features')?.scrollIntoView({ behavior: 'smooth' })} variant="outline" className="border-white/30 text-white bg-transparent hover:bg-white/10 rounded-sm h-12 px-8 text-base">
              See what's included
            </Button>
          </div>
        </div>
      </section>

      {/* What's included grid */}
      <section id="cfo-features" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-medium mb-4">What's included</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight">6 CFO-grade features. One bookkeeping app.</h2>
            <p className="text-[#5C6661] mt-4 max-w-2xl mx-auto">
              Out-of-the-box CFO insights that most apps make you build in spreadsheets or pay extra for.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, idx) => (
              <Card key={idx} className="p-6 hover:shadow-md transition-shadow" data-testid={`cfo-feature-${idx}`}>
                <div className="w-12 h-12 rounded bg-[#0A2B1D] text-white flex items-center justify-center mb-4">{f.icon}</div>
                <h3 className="font-semibold text-[#111614] mb-2">{f.title}</h3>
                <p className="text-sm text-[#5C6661] leading-relaxed">{f.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive scenario simulator */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-medium mb-4">Live preview</p>
            <h2 className="text-3xl md:text-4xl font-medium mb-4 tracking-tight">
              Scenario planning, without spreadsheets.
            </h2>
            <p className="text-[#5C6661] mb-6 leading-relaxed">
              Drag the sliders to see how a new hire, a price change, or a fundraise affects your runway. Real CFOs build this in Excel for hours. We do it in two clicks.
            </p>
            <ul className="space-y-3 text-sm text-[#111614]">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" /> Model up to 60 months ahead</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" /> Include one-time cash injections (fundraise, asset sale)</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" /> See exact month you'd run out of cash</li>
            </ul>
          </div>
          <ScenarioSimulator />
        </div>
      </section>

      {/* Weekly Brief sample */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <WeeklyBriefSample />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-medium mb-4">Weekly AI Finance Brief</p>
            <h2 className="text-3xl md:text-4xl font-medium mb-4 tracking-tight">
              Three things to know. Every Monday.
            </h2>
            <p className="text-[#5C6661] mb-6 leading-relaxed">
              Most founders don't have time to read every dashboard. Our AI CFO reads your data for you and emails the 3 most important takeaways — what changed, why it matters, and exactly what to do next.
            </p>
            <ul className="space-y-3 text-sm text-[#111614]">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" /> Personalized to your specific data (not generic tips)</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" /> One-click opt-out from your Profile</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" /> Included with Business and Business+ plans</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Board Pack sample */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-medium mb-4">Board / Investor Pack</p>
            <h2 className="text-3xl md:text-4xl font-medium mb-4 tracking-tight">
              Investor update in one click.
            </h2>
            <p className="text-[#5C6661] mb-6 leading-relaxed">
              Click a button → download a PDF with an AI-written Executive Summary, KPI table vs prior month, runway, top expense categories, and flagged anomalies. Ready to email to your board or investors.
            </p>
            <ul className="space-y-3 text-sm text-[#111614]">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" /> AI-written executive summary tailored to your numbers</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" /> Branded, professional layout</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" /> Pick any month, any year</li>
            </ul>
          </div>
          <BoardPackSample />
        </div>
      </section>

      {/* Differentiation strip */}
      <section className="py-16 px-6 bg-[#0A2B1D] text-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-[#B4C9BE] font-medium mb-4">Why this matters</p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight">
              Bookkeeping tells you what happened.<br /><span className="text-[#B4C9BE]">CFO Mode tells you what's coming.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { stat: '6', label: 'CFO-grade features built in', sub: 'Runway · Budgets · Anomalies · Scenarios · AI commentary · Weekly Brief' },
              { stat: 'Spreadsheets', label: 'What other tools require', sub: 'or expensive add-ons starting at $99/mo' },
              { stat: '$0 extra', label: 'For MyBookie users', sub: 'CFO Mode is included on Business and Business+ plans' },
            ].map((c, idx) => (
              <div key={idx} className="text-center p-6 bg-white/5 rounded border border-white/10">
                <div className="text-3xl font-bold text-[#B4C9BE] mb-2">{c.stat}</div>
                <div className="text-sm font-medium text-white mb-1">{c.label}</div>
                <div className="text-xs text-[#B4C9BE]">{c.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-4 tracking-tight">
            Ready to stop guessing about your finances?
          </h2>
          <p className="text-[#5C6661] mb-8 leading-relaxed">
            Connect your bank in 3 minutes. Start with the free plan, upgrade when you need CFO Mode.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleCTA} className="bg-[#0A2B1D] text-white hover:bg-[#254D3D] rounded-sm h-12 px-8 text-base" data-testid="cfo-hub-cta-bottom">
              {token ? 'Open CFO Mode' : 'Get started free'} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => navigate('/pricing')} variant="outline" className="rounded-sm h-12 px-8 text-base">
              See pricing
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-10 text-xs text-[#5C6661]">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> SOC 2 Type I/II</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Security audit: <span className="font-bold text-emerald-700">A+</span></span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-600" /> Bank-level encryption</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-600" /> No credit card to start</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CFOHub;
